import {
  buildFeedbackPresentationModel,
  detectInstrumentResponseFormat,
} from './instrumentResponsePresentation';

const VALID_NORMALIZED_ROLES = new Set(['participant', 'consensus']);

const rawResponse = result => result?.submittedData || result || {};

const normalizedIdentityKey = format => [
  format.protocolKey,
  format.protocolVersion || 'unknown',
  format.representationVersion || 'unknown',
].join('::');

export function sessionFeedbackRole(evaluation) {
  if (evaluation?.responseRepresentation === 'normalized') {
    return VALID_NORMALIZED_ROLES.has(evaluation.responseRole)
      ? evaluation.responseRole
      : 'incompatible';
  }
  return evaluation?.responseRole || (
    evaluation?.participantName === 'Consensus' ? 'consensus' : 'participant'
  );
}

function normalizedSessionEntry(result, selectedSample, role, format) {
  const response = rawResponse(result);
  return {
    responseRepresentation: 'normalized',
    responseRole: role,
    responseContract: response.responseContract,
    instrumentResponse: response.instrumentResponse,
    protocolKey: format.protocolKey,
    protocolVersion: format.protocolVersion,
    representationVersion: format.representationVersion,
    participantName: result?.participantName || 'Unknown',
    userId: result?.userId || 'Unknown',
    voiceSampleId: selectedSample?._id || response.voiceSampleId || null,
    presentation: buildFeedbackPresentationModel(response),
  };
}

function legacySessionEntry(result, selectedSample) {
  const response = rawResponse(result);
  const responseRole = result?.responseRole === 'consensus' || (
    !result?.responseRole && result?.participantName === 'Consensus'
  ) ? 'consensus' : 'participant';

  return {
    perceptualData: response.perceptualData || {},
    responseRepresentation: 'legacy',
    responseRole,
    participantName: result?.participantName || 'Unknown',
    userId: result?.userId || 'Unknown',
    voiceSampleId: selectedSample?._id || response.voiceSampleId || null,
  };
}

function derivedAggregateEntry(result) {
  const units = result?.units || result?.payload || {};
  const numericUnits = Object.fromEntries(Object.entries(units).flatMap(([key, unit]) => {
    const value = unit?.value ?? unit?.numericAggregateValue;
    return Number.isFinite(value) ? [[key, {
      value,
      observationCount: unit?.observationCount ?? null,
    }]] : [];
  }));

  return {
    responseRole: 'derivedAggregate',
    status: result?.status || (Object.keys(numericUnits).length ? 'calculated' : 'unavailable'),
    algorithmId: result?.algorithmId || null,
    algorithmVersion: result?.algorithmVersion || null,
    protocolIdentity: result?.protocolIdentity || null,
    units: numericUnits,
    warnings: Array.isArray(result?.warnings) ? result.warnings : [],
  };
}

export function classifySessionFeedback(results = [], selectedSample = null) {
  const directParticipants = [];
  const explicitConsensus = [];
  const derivedAggregates = [];
  const incompatibleDiagnostics = [];
  const legacyResponses = [];
  let cohortIdentity = null;

  results.forEach((result, index) => {
    const explicitRole = result?.responseRole || result?.submittedData?.responseRole || null;
    if (explicitRole === 'derivedAggregate') {
      derivedAggregates.push(derivedAggregateEntry(result?.submittedData || result));
      return;
    }

    const response = rawResponse(result);
    const format = detectInstrumentResponseFormat(response);
    if (format.kind === 'legacy') {
      const legacy = legacySessionEntry(result, selectedSample);
      legacyResponses.push(legacy);
      (legacy.responseRole === 'consensus' ? explicitConsensus : directParticipants).push(legacy);
      return;
    }

    if (format.kind !== 'normalized' || !VALID_NORMALIZED_ROLES.has(explicitRole)) {
      incompatibleDiagnostics.push({
        index,
        code: format.kind === 'normalized'
          ? 'NORMALIZED_SESSION_RESPONSE_ROLE_INVALID'
          : 'SESSION_RESPONSE_REPRESENTATION_UNSUPPORTED',
      });
      return;
    }

    const identity = normalizedIdentityKey(format);
    if (!cohortIdentity) cohortIdentity = identity;
    if (identity !== cohortIdentity) {
      incompatibleDiagnostics.push({
        index,
        code: 'NORMALIZED_SESSION_PROTOCOL_MISMATCH',
        protocolKey: format.protocolKey,
        protocolVersion: format.protocolVersion,
      });
      return;
    }

    const entry = normalizedSessionEntry(result, selectedSample, explicitRole, format);
    (explicitRole === 'consensus' ? explicitConsensus : directParticipants).push(entry);
  });

  return {
    directParticipants,
    explicitConsensus,
    derivedAggregates,
    incompatibleDiagnostics,
    legacyResponses,
  };
}

// Legacy-only compatibility adapter. Normalized envelopes must use
// classifySessionFeedback so they are never projected into perceptualData.
export const adaptSessionFeedback = (results, selectedSample) =>
  classifySessionFeedback(results, selectedSample).legacyResponses;
